import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import { GetUserData, logoutWithAudit, setAccessToken, setWorkspaceSelectionRequired } from '../../redux/usersSlice';
import { setProjectImg } from '../../redux/projectDataSlice';
import { setDocs } from '../../redux/documentsSlice';
import { API_BASE_URL, SETUP_2FA, GET_QR_CODE, SEND_SMS_CODE } from '../../apiServices/api_routes';
import { SpinnerIos16Regular } from '@fluentui/react-icons';
import CountryCodeSelector from '../CommonComponents/CountryCodeSelector';
import styles from '../Register/css/Register.module.css';

const Setup2FAModal = ({ isOpen, onClose, userEmail, isUpdate = false }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [twoFAMethod, setTwoFAMethod] = useState('qr');
    const [smsStep, setSmsStep] = useState('phone');
    const [loading, setLoading] = useState(false);
    const [qrVerificationCode, setQrVerificationCode] = useState(['', '', '', '', '', '']);
    const [smsVerificationCode, setSmsVerificationCode] = useState(['', '', '', '', '', '']);
    const [smsData, setSmsData] = useState({ code: '', number: '' });
    const [resendSecondsRemaining, setResendSecondsRemaining] = useState(0);
    const [setupLockoutSecondsRemaining, setSetupLockoutSecondsRemaining] = useState(0);
    const [showSetupLockScreen, setShowSetupLockScreen] = useState(false);
    const [qrData, setQrData] = useState('');
    const [qrLoading, setQrLoading] = useState(false);
    const qrInputRefs = useRef([]);
    const smsInputRefs = useRef([]);
    const isResendDisabled = resendSecondsRemaining > 0;
    const isSetupLocked = setupLockoutSecondsRemaining > 0;
    const setupLockoutMinutes = Math.floor(setupLockoutSecondsRemaining / 60);
    const setupLockoutSeconds = setupLockoutSecondsRemaining % 60;
    const setupLockoutCountdown = `${setupLockoutMinutes}:${String(setupLockoutSeconds).padStart(2, '0')}`;

    useEffect(() => {
        if (resendSecondsRemaining <= 0) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setResendSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [resendSecondsRemaining]);

    useEffect(() => {
        if (!isSetupLocked) {
            return undefined;
        }
        const intervalId = window.setInterval(() => {
            setSetupLockoutSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [isSetupLocked]);

    const handleSetupLockoutError = error => {
        if (error?.response?.status !== 423) {
            return false;
        }
        const seconds = Number(error?.response?.data?.retry_after_seconds);
        const remaining = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 5 * 60;
        setSetupLockoutSecondsRemaining(remaining);
        setShowSetupLockScreen(true);
        toast.error('Too many invalid verification attempts. Please try again later.');
        return true;
    };

    const fetchQrCode = async () => {
        if (!userEmail) return;
        try {
            setQrLoading(true);
            const response = await axios.post(`${API_BASE_URL}${GET_QR_CODE}`, {
                email: userEmail,
                update: Boolean(isUpdate),
            });
            setQrData(response.data.qr_data);
        } catch (error) {
            console.error('Error fetching QR code:', error);
            toast.error('Failed to fetch QR code');
        } finally {
            setQrLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && twoFAMethod === 'qr' && userEmail) {
            fetchQrCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchQrCode reads isUpdate; include when modal mode changes
    }, [isOpen, twoFAMethod, userEmail, isUpdate]);

    const handleQrCodeChange = (index, event) => {
        const newCode = [...qrVerificationCode];
        newCode[index] = event.target.value;
        setQrVerificationCode(newCode);
        if (event.target.value && index < 5) qrInputRefs.current[index + 1]?.focus();
    };

    const handleQrKeyDown = (index, event) => {
        if (event.key === 'Backspace' && !qrVerificationCode[index] && index > 0) {
            qrInputRefs.current[index - 1]?.focus();
        }
    };

    const handleQrPaste = event => {
        const pastedData = event.clipboardData.getData('Text');
        if (pastedData.length === 6 && qrInputRefs.current[0] === document.activeElement) {
            setQrVerificationCode(pastedData.split('').slice(0, 6));
        }
    };

    const handleSmsCodeChange = (index, event) => {
        const newCode = [...smsVerificationCode];
        newCode[index] = event.target.value;
        setSmsVerificationCode(newCode);
        if (event.target.value && index < 5) smsInputRefs.current[index + 1]?.focus();
    };

    const handleSmsKeyDown = (index, event) => {
        if (event.key === 'Backspace' && !smsVerificationCode[index] && index > 0) {
            smsInputRefs.current[index - 1]?.focus();
        }
    };

    const handleSmsPaste = event => {
        const pastedData = event.clipboardData.getData('Text');
        if (pastedData.length === 6 && smsInputRefs.current[0] === document.activeElement) {
            setSmsVerificationCode(pastedData.split('').slice(0, 6));
        }
    };

    const handleSmsChange = (field, value) => {
        setSmsData(prev => ({ ...prev, [field]: value }));
    };

    const sessionFrom2FAResponse = async response => {
        const access = response?.data?.access;
        const refresh = response?.data?.refresh;
        if (access && refresh) {
            dispatch(setAccessToken(access));
            Cookies.set('refresh', refresh, { secure: true, sameSite: 'strict' });
            await dispatch(GetUserData());
            toast.success(isUpdate ? '2FA updated successfully.' : '2FA setup completed successfully.');
            handleClose();
            return true;
        }
        return false;
    };

    const logoutAndRedirectToLogin = async () => {
        handleClose();
        Cookies.set('refresh', '');
        dispatch(setProjectImg(''));
        dispatch(setDocs([]));
        dispatch(setWorkspaceSelectionRequired(false));
        await dispatch(logoutWithAudit());
        navigate('/login', { replace: true });
    };

    const handleQrActivation = async () => {
        const verificationCode = qrVerificationCode.join('');
        if (verificationCode.length !== 6) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }
        try {
            setLoading(true);
            const response = await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: userEmail,
                mfa_method: 'app',
                verification_code: verificationCode,
                update: Boolean(isUpdate),
            });
            if (await sessionFrom2FAResponse(response)) {
                return;
            }
            toast.success(isUpdate ? '2FA updated successfully! Please sign in again.' : '2FA setup completed successfully! Please sign in again.');
            logoutAndRedirectToLogin();
        } catch (error) {
            if (handleSetupLockoutError(error)) {
                return;
            }
            const errorMessage = error.response?.data?.error || 'Failed to activate 2FA';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSendSmsCode = async () => {
        if (!smsData.code || !smsData.number) {
            toast.error('Please enter your phone number');
            return;
        }
        try {
            setLoading(true);
            const phoneNumber = smsData.code + smsData.number;
            await axios.post(`${API_BASE_URL}${SEND_SMS_CODE}`, {
                email: userEmail,
                phone_number: phoneNumber,
            });
            setSmsStep('verification');
            setResendSecondsRemaining(30);
            toast.success('Verification code sent to your phone!');
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to send verification code. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSmsActivation = async () => {
        const verificationCode = smsVerificationCode.join('');
        if (verificationCode.length !== 6) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }
        try {
            setLoading(true);
            const phoneNumber = smsData.code + smsData.number;
            const response = await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: userEmail,
                mfa_method: 'sms_twilio',
                phone_number: phoneNumber,
                verification_code: verificationCode,
                update: Boolean(isUpdate),
            });
            if (await sessionFrom2FAResponse(response)) {
                return;
            }
            toast.success(isUpdate ? '2FA updated successfully! Please sign in again.' : '2FA setup completed successfully! Please sign in again.');
            logoutAndRedirectToLogin();
        } catch (error) {
            if (handleSetupLockoutError(error)) {
                return;
            }
            const errorMessage = error.response?.data?.error || 'Invalid verification code. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (twoFAMethod === 'sms' && smsStep === 'verification') {
            setSmsStep('phone');
        } else if (twoFAMethod === 'sms' && smsStep === 'phone') {
            setTwoFAMethod('qr');
        } else {
            handleClose();
        }
    };

    const handleClose = () => {
        setTwoFAMethod('qr');
        setSmsStep('phone');
        setQrVerificationCode(['', '', '', '', '', '']);
        setSmsVerificationCode(['', '', '', '', '', '']);
        setSmsData({ code: '', number: '' });
        setResendSecondsRemaining(0);
        setSetupLockoutSecondsRemaining(0);
        setShowSetupLockScreen(false);
        setQrData('');
        onClose();
    };

    if (!isOpen) return null;

    const otpInputStyle = {
        width: '14%',
        height: '48px',
        fontSize: '18px',
        fontFamily: 'monospace',
        fontWeight: 'bold',
        border: 'none',
        borderBottom: '1px solid var(--Neutral-Gray-6)',
        outline: 'none',
        textAlign: 'center',
        borderRadius: '0px',
    };

    const qrContainerStyle = {
        width: '168px',
        height: '168px',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
    };

    const qrImageContainerStyle = {
        width: '168px',
        height: '168px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '8px',
    };

    if (showSetupLockScreen) {
        return (
            <Modal show={isOpen} onHide={handleClose} centered className="setup-2fa-modal">
                <Modal.Body className={styles.formBody} style={{ padding: '24px', overflow: 'visible' }}>
                    <h5 className={styles.formLabel}>Your Account Is Temporarily Locked</h5>
                    <p className={styles.formSubtitle}>
                        For security reasons, your account has been temporarily locked after multiple unsuccessful verification attempts.
                        Please try signing in again in {setupLockoutCountdown}.
                    </p>
                    <div className="d-flex align-items-center justify-content-between mt-4">
                        <button
                            type="button"
                            onClick={() => setShowSetupLockScreen(false)}
                            disabled={isSetupLocked}
                            className={styles.submitBtn}
                            style={isSetupLocked ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}>
                            Sign-in
                        </button>
                        <a href="mailto:LensSupport@beyond-ma.com" className={styles.secondaryBtnLink}>
                            Contact Support
                        </a>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    // QR Code Screen
    if (twoFAMethod === 'qr') {
        return (
            <Modal show={isOpen} onHide={handleClose} centered className="setup-2fa-modal">
                <Modal.Body className={styles.formBody} style={{ padding: '24px', overflow: 'visible' }}>
                    <h5 className={styles.formLabel}>{isUpdate ? 'Update 2FA' : 'Set up 2FA'}</h5>
                    <p className={styles.formSubtitle}>
                        For heightened security measures, kindly scan the provided code using your authenticator app and enter the corresponding verification code below. We will
                        use this method by default.
                    </p>

                    <div className="text-center">
                        <div className="d-flex justify-content-center mb-3">
                            {qrLoading ? (
                                <div style={qrContainerStyle}>
                                    <SpinnerIos16Regular className="rotateLoader" />
                                </div>
                            ) : qrData ? (
                                <div style={qrImageContainerStyle}>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`}
                                        alt="QR Code"
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            ) : (
                                <div style={qrContainerStyle}>
                                    <span className="text-muted">QR Code</span>
                                </div>
                            )}
                        </div>
                        <p className="text-muted small">Open your authenticator app and scan this QR code to add your Lens account.</p>
                    </div>

                    <div className="mb-3">
                        <label className={styles.fieldLabel}>Enter verification code</label>
                        <div className="d-flex justify-content-center">
                            <div className="d-flex" style={{ gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                                {[0, 1, 2, 3, 4, 5].map(index => (
                                    <input
                                        key={index}
                                        type="text"
                                        maxLength="1"
                                        value={qrVerificationCode[index]}
                                        onChange={e => handleQrCodeChange(index, e)}
                                        onKeyDown={e => handleQrKeyDown(index, e)}
                                        ref={ref => (qrInputRefs.current[index] = ref)}
                                        onPaste={handleQrPaste}
                                        className={'form-control text-center ' + styles.inputField}
                                        style={otpInputStyle}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-4">
                        <button onClick={handleQrActivation} disabled={loading || qrVerificationCode.join('').length !== 6} className={styles.submitBtn}>
                            {isUpdate ? 'Update' : 'Activate account'} {loading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                        </button>
                        <span role="button" onClick={() => setTwoFAMethod('sms')} className={styles.secondaryBtnLink}>
                            Use SMS authentication
                        </span>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    // SMS Phone Number Screen
    if (twoFAMethod === 'sms' && smsStep === 'phone') {
        return (
            <Modal show={isOpen} onHide={handleClose} centered className="setup-2fa-modal">
                <Modal.Body className={styles.formBody} style={{ padding: '24px', overflow: 'visible' }}>
                    <h5 className={styles.formLabel}>{isUpdate ? 'Update 2FA' : 'Set up 2FA'}</h5>
                    <p className={styles.formSubtitle}>
                        For heightened security measures, add your mobile number below to receive verification code. We will use this method by default. Your mobile number will
                        only be used for account security. Standard SMS charges will apply.
                    </p>

                    <div className="d-flex">
                        <div style={{ width: '120px' }} className="me-2">
                            <div>
                                <p className={styles.fieldLabel}>Code</p>
                                <CountryCodeSelector
                                    value={smsData?.code}
                                    onChange={value => handleSmsChange('code', value)}
                                    placeholder="Please Select"
                                    className=""
                                    name="code"
                                    ariaLabel="Country code"
                                    insideModal
                                />
                            </div>
                        </div>
                        <div className="w-100">
                            <div>
                                <p className={styles.fieldLabel}>Mobile Number</p>
                                <input
                                    type="number"
                                    placeholder="Mobile Number"
                                    value={smsData?.number}
                                    onChange={e => handleSmsChange('number', e.target.value)}
                                    className={'form-control ' + styles.inputField}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-4">
                        <button onClick={handleSendSmsCode} disabled={!smsData?.code || !smsData?.number || loading} className={styles.submitBtn}>
                            Send Code {loading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                        </button>
                        <span role="button" onClick={handleBack} className={styles.secondaryBtnLink}>
                            Back
                        </span>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    // SMS Verification Code Screen
    return (
        <Modal show={isOpen} onHide={handleClose} centered className="setup-2fa-modal">
            <Modal.Body className={styles.formBody} style={{ padding: '24px', overflow: 'visible' }}>
                <h5 className={styles.formLabel}>Enter verification code</h5>
                <p className={styles.formSubtitle}>The verification code is sent on your mobile number</p>

                <div className="mb-3">
                    <label className={styles.fieldLabel}>Enter verification code</label>
                    <div className="d-flex justify-content-center">
                        <div className="d-flex" style={{ gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                            {[0, 1, 2, 3, 4, 5].map(index => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={smsVerificationCode[index]}
                                    onChange={e => handleSmsCodeChange(index, e)}
                                    onKeyDown={e => handleSmsKeyDown(index, e)}
                                    ref={ref => (smsInputRefs.current[index] = ref)}
                                    onPaste={handleSmsPaste}
                                    className={'form-control text-center ' + styles.inputField}
                                    style={otpInputStyle}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-4">
                    <button onClick={handleSmsActivation} disabled={smsVerificationCode.join('').length !== 6 || loading} className={styles.submitBtn}>
                        {isUpdate ? 'Update' : 'Activate account'} {loading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                    </button>
                    <div className="d-flex flex-column align-items-end">
                        <span role="button" onClick={handleBack} className={styles.secondaryBtnLink}>
                            Back
                        </span>
                        <span
                            role="button"
                            onClick={isResendDisabled ? undefined : handleSendSmsCode}
                            className={styles.secondaryBtnLink}
                            style={{ fontSize: '14px', marginTop: '8px', color: isResendDisabled ? 'var(--Neutral-Gray-6)' : 'var(--Brand-Secondary)', pointerEvents: isResendDisabled ? 'none' : 'auto' }}>
                            {isResendDisabled ? `Resend (${resendSecondsRemaining})` : 'Resend'}
                        </span>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default Setup2FAModal;
